import { useCallback, useMemo, useState } from 'react';

import { useGetDisbursementPayoutBatches } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';
import { useGlobalFilters } from 'hooks/useGlobalFilters';

import { TableLayout } from 'components/ui/templates/TableLayout/TableLayout';

import { apiParams } from './apiAdapter';
import { BatchesHeader } from './BatchesHeader';
import { BatchesTable } from './BatchesTable';
import type { FiltersState } from './localState';
import { useLocalState } from './localState';

export const Batches = () => {
  const { isBatchApprovalRequired } = useFeatureAccessControl();
  const [refreshKey, setRefreshKey] = useState(0);

  const organisationId = useOrganisationId<number>();

  const {
    state: localState,
    dispatch,
    updateCursorAfter,
    setFilters: updateFilters
  } = useLocalState(isBatchApprovalRequired);

  const setFilters = useCallback(
    (filters: FiltersState) => {
      dispatch(updateFilters(filters));
    },
    [dispatch, updateFilters]
  );

  const { filters: globalFilters } = useGlobalFilters();

  const params = useMemo(
    () =>
      apiParams({
        ...localState,
        ...globalFilters
      }),
    [localState, globalFilters]
  );

  const {
    data: batches,
    isFetching,
    refetch
  } = useGetDisbursementPayoutBatches(organisationId, params);

  /**
   * There are currently 2 ways of doing a refetch:
   * Either you pass the refetch function to a child component and call it from there.
   * Or you invalidate the key -> by doing queryClient.invalidateKey and all hooks that are related to that key will automatically refetch.
   * Both situations introduced the same issue, the first one has the following behavior:
   * If you refetch, because of the infinite scroll logic, the new uploaded batch would show up at the bottom of the list. The user most likely will not notice that and it's not a good UX at all.
   * The other issue that might be introduced.
   */
  const refetchBatchesWithRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    return refetch();
  }, [refetch]);

  return (
    <TableLayout
      header={
        <BatchesHeader
          filters={localState.filters}
          setFilters={setFilters}
          isBatchApprovalRequired={isBatchApprovalRequired}
        />
      }
      content={
        <BatchesTable
          globalFilters={globalFilters}
          refetchBatches={refetchBatchesWithRefresh}
          data={batches}
          isLoading={isFetching}
          localState={localState}
          setFilters={setFilters}
          updateCursorAfter={updateCursorAfter}
          isBatchApprovalRequired={isBatchApprovalRequired}
          refreshKey={refreshKey}
        />
      }
    />
  );
};

export type RefetchBatchesType = ReturnType<
  typeof useGetDisbursementPayoutBatches
>['refetch'];

export type GlobalFiltersType = ReturnType<typeof useGlobalFilters>['filters'];
